import { useRef, useEffect, useState } from 'react';

const carpetTypes = [
  { name: 'Plush / Saxony Carpet', desc: 'Luxuriously soft and smooth — perfect for bedrooms, living rooms, and formal spaces.' },
  { name: 'Berber / Loop Pile Carpet', desc: 'Durable, stain-resistant looped construction ideal for high-traffic areas and hallways.' },
  { name: 'Frieze / Textured Carpet', desc: 'Twisted fibers create a casual, stylish look that hides footprints and vacuum marks.' },
  { name: 'Carpet Tiles', desc: 'Modular, replaceable tiles perfect for commercial spaces, offices, and specialty environments.' },
  { name: 'Marine & Boat Carpet', desc: 'UV-resistant, moisture-proof carpet engineered for marine environments and outdoor use.' },
  { name: 'Specialty Turf', desc: 'High-performance synthetic turf for recreational, commercial, and specialty applications.' },
];

const benefits = [
  { icon: 'ri-home-heart-line', title: 'Residential Installs', desc: 'Bedrooms, living rooms, basements, and whole-home carpet installations.' },
  { icon: 'ri-building-line', title: 'Commercial Projects', desc: 'Offices, retail spaces, hospitality, and large-scale commercial flooring.' },
  { icon: 'ri-refresh-line', title: 'Full Replacements', desc: 'Old carpet removed, subfloor prepped, and new carpet installed to perfection.' },
  { icon: 'ri-scissors-cut-line', title: 'Custom Fitting', desc: 'Precision cutting and seaming for irregular rooms, stairs, and transitions.' },
];

export default function ServiceDetails() {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} id="services" className="py-24 lg:py-32 bg-cream">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          className="max-w-2xl mb-16"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="section-tag mb-4 block">Service Details</span>
          <h2 className="section-heading mb-6">
            Carpet Types &amp; Solutions
          </h2>
          <p className="text-warm-gray text-base leading-relaxed">
            Value Carpet Inc installs a full range of carpet types for every application — from plush residential to heavy-duty commercial and specialty marine installations.
          </p>
        </div>

        {/* Carpet types grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
          {carpetTypes.map((type, i) => (
            <div
              key={type.name}
              className="bg-white rounded-lg p-7 border border-cream-dark hover:border-gold/30 transition-colors duration-300"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.6s ease ${0.08 + i * 0.08}s, transform 0.6s ease ${0.08 + i * 0.08}s`,
              }}
            >
              <div className="w-8 h-0.5 bg-gold mb-5" />
              <h3 className="font-serif text-lg text-charcoal mb-3">{type.name}</h3>
              <p className="text-warm-gray text-sm leading-relaxed">{type.desc}</p>
            </div>
          ))}
        </div>

        {/* Split section — image + benefits */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div
            className="rounded-lg overflow-hidden"
            style={{
              height: '500px',
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(-28px)',
              transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
            }}
          >
            <img
              src="https://public.readdy.ai/ai/img_res/4878deb7524198509eeca0b9b542cff9.jpg"
              alt="Professional carpet installation"
              className="w-full h-full object-cover object-center"
            />
          </div>

          <div
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(28px)',
              transition: 'opacity 0.8s ease 0.3s, transform 0.8s ease 0.3s',
            }}
          >
            <span className="section-tag mb-4 block">What We Cover</span>
            <h2 className="section-heading mb-8">
              Full-Service Carpet<br />
              <em className="text-gold not-italic">Installation & Replacement</em>
            </h2>
            <div className="space-y-6">
              {benefits.map((b) => (
                <div key={b.title} className="flex gap-4 items-start">
                  <span className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-lg bg-charcoal">
                    <i className={`${b.icon} text-gold text-base`}></i>
                  </span>
                  <div>
                    <p className="font-semibold text-charcoal text-sm mb-1">{b.title}</p>
                    <p className="text-warm-gray text-sm leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
